import { Money } from '~/types/money';
import type {
  AltriEntiTaxDebitCreditFormInput,
  AltriEntiTaxDebitCreditFormOutput,
} from '~/views/bookkeeping/taxes/TaxDebitCreditDialog/AltriEntiTaxDebitCreditForm';
import type {
  ErarioDebitCreditFormInput,
  ErarioDebitCreditFormOutput,
} from '~/views/bookkeeping/taxes/TaxDebitCreditDialog/ErarioTaxDebitCreditForm';
import type {
  IMUAltriEntiTaxDebitCreditFormInput,
  IMUAltriEntiTaxDebitCreditFormOutput,
} from '~/views/bookkeeping/taxes/TaxDebitCreditDialog/IMUAltriEntiTaxDebitCreditForm';
import type {
  INAILAltriEntiTaxDebitCreditFormInput,
  INAILAltriEntiTaxDebitCreditFormOutput,
} from '~/views/bookkeeping/taxes/TaxDebitCreditDialog/INAILAltriEntiTaxDebitCreditForm';
import type {
  INPSTaxDebitCreditFormInput,
  INPSTaxDebitCreditFormOutput,
} from '~/views/bookkeeping/taxes/TaxDebitCreditDialog/INPSTaxDebitCreditForm';
import type {
  RegioniTaxDebitCreditFormInput,
  RegioniTaxDebitCreditFormOutput,
} from '~/views/bookkeeping/taxes/TaxDebitCreditDialog/RegioniTaxDebitCreditForm';
import { Account } from './account';

export const TaxDebitCreditResourceType = 'tax-debit-credit';

export const F24QueryKeys = {
  all: ['F24'],
  taxDebitCredits: ['TaxDebitCredits'],
  taxDebitCreditsTable: (companyId: string, filters: TaxDebitCreditsQueryFilters) => [
    'TaxDebitCredits',
    { companyId },
    'Table',
    filters,
  ],
  taxDebitCreditsMetadata: (companyId: string, filters: TaxDebitCreditsQueryFilters) => [
    'TaxDebitCredits',
    { companyId },
    'Metadata',
    filters,
  ],
  f24List: (companyId: string, filters?: F24QueryFilters) => ['F24', { companyId }, 'List', filters],
  isFirstPayment: (companyId: string) => ['F24', { companyId }, 'IsFirstPayment'],
  ocrList: (companyId: string) => ['F24', { companyId }, 'OcrList'] as const,
  ocrItem: (companyId: string, ocrJobId: string) => ['F24', { companyId }, 'OcrItem', ocrJobId] as const,
  ocrPage: (companyId: string, ocrJobId: string, page: number) =>
    ['F24', { companyId }, 'OcrPage', ocrJobId, page] as const,
  single: (id: string) => ['F24', id],
  details: (id: string) => ['F24', id, 'Details'] as const,
  withPages: (id: string) => ['F24', id, 'WithPages'] as const,
  sddCutoff: (paymentDate: string) => ['F24', 'SddCutoff', paymentDate] as const,
  singleTaxDebitCredit: (id: string) => ['TaxDebitCredits', id],
  codiciTributo: (filter: {
    search?: string;
    section?: CodiceTributoSection;
    modality?: CodiceTributoModality;
    codes?: string[];
  }) => ['CodiciTributo', 'Search', filter],
  codiciRegione: (search: string) => ['CodiciRegione', 'Search', { search }],
  codiciSedeINPS: (search: string) => ['CodiciSedeINPS', 'Search', { search }],
  causaliContributoINPS: (search: string, modality?: string) => [
    'CausaliContributoINPS',
    'Search',
    { search, modality },
  ],
};
export interface F24QueryFilters {
  source?: F24Source[];
}

export enum F24SectionId {
  ERARIO = 'erario',
  INPS = 'inps',
  REGIONI = 'regioni',
  IMU_ALTRI_ENTI = 'imu_altri_enti',
  INAIL = 'inail_altri_enti',
  ALTRI_ENTI = 'altri_enti',
}

interface DettaglioPagamentoBase {
  codiceTributo: string;
  annoPeriodoRiferimento?: string | null;
  rateazioneRegioneProvMeseRif?: string | null;
  importoADebitoVersato?: number | null;
  importoACreditoCompensato?: number | null;
}

interface RiferimentoAtto {
  codiceUfficio: string;
  codiceAtto: string;
  tipoAtto: string;
  elementiIdentificativi: string;
}

interface DettaglioErario extends DettaglioPagamentoBase {}

interface DettaglioINPS {
  codiceSede?: string | null;
  causaleContributo?: string | null;
  matricolaInpsFilialeAzienda?: string | null;
  periodoRiferimentoDa?: string | null;
  periodoRiferimentoA?: string | null;
  importoACreditoCompensato?: number | null;
  importoADebitoVersato?: number | null;
}

interface DettaglioRegioni extends DettaglioPagamentoBase {
  codiceRegione?: string | null;
}

interface DettaglioImuAltriTributiLocali extends DettaglioPagamentoBase {
  codiceEnteLocale?: string | null;
  ravvedimento?: boolean | null;
  immobiliVariati?: boolean;
  acconto?: boolean;
  saldo?: boolean;
  numeroImmobili?: number | null;
  detrazione?: number | null;
}

interface DettaglioAltriEnti extends DettaglioPagamentoBase {
  codiceSede?: string | null;
  codiceDitta?: string | null;
  codiceControllo?: string | null;
  codiceContributo?: string | null;
  numeroRiferimento?: string | null;
  codiceEnte?: string | null;
  codicePosizione?: string | null;
  periodoRiferimentoDa?: string | null;
  periodoRiferimentoA?: string | null;
}

interface DettaglioAccise extends DettaglioPagamentoBase, RiferimentoAtto {
  codiceIdentificativoAccise?: string | null;
  meseAccise?: string | null;
  codiceEnte?: string | null;
  provincia?: string | null;
}

interface DettaglioElementiIdentificativi extends DettaglioPagamentoBase, RiferimentoAtto {
  tipoElide: string;
}

interface Anagrafica {
  codiceFiscale?: string | null;
  cognomeDenominazioneRagioneSociale?: string | null;
  nome?: string | null;
  dataNascita?: string | null;
  sesso?: 'M' | 'F' | null;
  comuneOStatoEsteroNascita?: string | null;
  provinciaNascita?: string | null;
  comuneSede?: string | null;
  provinciaSede?: string | null;
  viaENumeroCivicoSede?: string | null;
  capSede?: string | null;
  email?: string | null;
  telefono?: string | null;
}

export interface Contribuente extends Anagrafica {
  annoImpostaNonCoincidenteAnnoSolare?: boolean;
}

export interface Coobbligato extends Anagrafica {
  codiceIdentificativoCoobbligato?: string | null;
}

export interface SezioneErario {
  dettagli?: DettaglioErario[] | null;
  riferimentoAtto?: RiferimentoAtto | null;
  totaleADebito?: number | null;
  totaleACredito?: number | null;
  saldoSezioneErario?: number | null;
}

export interface SezioneINPS {
  dettagli?: DettaglioINPS[] | null;
  totaleADebitoInps?: number | null;
  totaleACreditoCompensatoInps?: number | null;
}

export interface SezioneRegioni {
  dettagli?: DettaglioRegioni[] | null;
  totaleADebitoRegioni?: number | null;
  totaleACreditoCompensatoRegioni?: number | null;
}

export interface SezioneImuAltriTributiLocali {
  dettagli?: DettaglioImuAltriTributiLocali[] | null;
  totaleADebitoImuAltri?: number | null;
  totaleACreditoCompensatoImuAltri?: number | null;
}

export interface SezioneAltriEnti {
  dettagli?: DettaglioAltriEnti[] | null;
  totaleADebitoAltriEnti?: number | null;
  totaleACreditoCompensatoAltriEnti?: number | null;
}

export interface SezioneAcciseMonopoli {
  dettagli?: DettaglioAccise[] | null;
  totaleADebitoAccise?: number | null;
  saldoSezioneAccise?: number | null;
  totaleACreditoCompensatoAccise?: number | null;
}

export interface SezioneElide {
  dettagli?: DettaglioElementiIdentificativi[] | null;
  saldoSezioneElide?: number | null;
}

export interface DatiPagamento {
  dataPagamento: string;
  codiceFiscalePagatore?: string | null;
  ibanAddebito?: string | null;
  firmaObbligatoria: boolean;
  abi?: string | null;
  cab?: string | null;
  numeroConto?: string | null;
  estremiVersamentoBollettinoPostale?: string | null;
}

export interface TotaliComplessivi {
  importoTotaleADebito?: number | null;
  importoTotaleACreditoCompensato?: number | null;
  saldoFinale?: number | null;
  codiceFiscaleIntermediario?: string | null;
  impegnoAllaPresentazioneTelematica?: boolean;
  dataImpegnoPresentazione?: string | null;
}

export enum F24Status {
  TO_BE_TRANSMITTED = 'TO_BE_TRANSMITTED',
  TRANSMITTED = 'TRANSMITTED',
  RECEIVED = 'RECEIVED',
  PAID = 'PAID',
  EXECUTED = 'EXECUTED',
  CANCELLED = 'CANCELLED',
  DISCARDED = 'DISCARDED',
  REJECTED = 'REJECTED',
  SUSPENDED = 'SUSPENDED',
  DEBIT_REJECTED = 'DEBIT_REJECTED',
  TO_BE_WITHDRAWN = 'TO_BE_WITHDRAWN',
}

export type F24UploadStatus = 'WAITING_UPLOAD' | 'WAITING_OCR' | 'WAITING_REVIEW' | 'REVIEWED' | 'CANCELED';

export const F24OcrUploadResourceType = 'f24-upload';

export interface F24OcrUpload {
  id: string;
  ocrJobId: string;
  fileName: string;
  name: string;
  status: F24UploadStatus;
  totalAmount: string | null;
  paymentDate: string | null;
  modelType: string | null;
  pagesCount: number | null;
  createdAt: string;
}

export enum F24ReceiptStatus {
  RECEIVED = 'RECEIVED',
  DEBITED = 'DEBITED',
  EXECUTED = 'EXECUTED',
  REJECTED = 'REJECTED',
  DISCARDED = 'DISCARDED',
  CANCELLED = 'CANCELLED',
  SUSPENDED = 'SUSPENDED',
}

export interface F24Receipt {
  id: string;
  fileName: string;
  receiptDate: string;
  status: F24ReceiptStatus;
  pdfUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface F24UploadPage {
  id: string;
  pageNumber: number;
  status: F24Status;
  exportedAt: string | null;
  totalBalance: Money | null;
  receipts?: F24Receipt[];
  createdAt: string;
  updatedAt: string;
}

export interface F24SourceUpload {
  id: string;
  fileName: string;
  name: string;
  pagesCount: number | null;
  modelType?: string | null;
  pages?: F24UploadPage[];
  createdAt: string;
  updatedAt: string;
}

export interface F24Account {
  id: string;
  nickname?: string;
}

export interface DirectDebitCollection {
  id: string;
}

export interface F24 {
  id: string;
  companyId: string;
  name: string | null;
  modelType?: string | null;
  paymentDate: string;
  iban: string | null;
  jsonPath: string;
  account?: F24Account;
  sourceUpload?: F24SourceUpload;
  directDebitCollection?: DirectDebitCollection | null;
  createdAt: string;
  updatedAt: string;
}

export interface F24Details {
  contribuente?: Contribuente | null;
  coobbligato?: Coobbligato | null;
  sezioneErario?: SezioneErario | null;
  sezioneInps?: SezioneINPS | null;
  sezioneRegioni?: SezioneRegioni | null;
  sezioneImuAltriTributiLocali?: SezioneImuAltriTributiLocali | null;
  sezioneAltriEnti?: SezioneAltriEnti | null;
  sezioneAcciseMonopoli?: SezioneAcciseMonopoli | null;
  sezioneElide?: SezioneElide | null;
  datiPagamento?: DatiPagamento | null;
  totaliComplessivi?: TotaliComplessivi | null;
}

export enum TaxDebitCreditSourceContext {
  Withholding = 'withholding',
}

interface TaxDebitCreditSource {
  createdAt: string;
  id: string;
  sourceContext: TaxDebitCreditSourceContext | null;
  sourceId: string;
  updatedAt: string;
}

export interface BaseTaxDebitCredit {
  id: string;
  companyId: string;
  bookkeepingAccountId: string;
  tribute: string;
  description: string | null;
  annoRiferimento: string | null;
  debitAmount: Money | null;
  creditAmount: Money | null;
  remainingDebit: Money | null;
  remainingCredit: Money | null;
  validFrom: string | null;
  dueDate: string | null;
  createdAt: string;
  updatedAt: string;
  vatSettlementPeriodId: string | null;
  sources: TaxDebitCreditSource[];
}

export interface ErarioDetails {
  codice_tributo: string | null;
  anno_periodo_riferimento: string | null;
  importo_a_debito_versato: string | null;
  importo_a_credito_compensato: string | null;
  codice_ufficio: string | null;
  codice_atto: string | null;
  tipo_atto?: string | null;
  elementi_identificativi?: string | null;
  rateazione_regione_prov_mese_rif?: string | null;
}

export interface INPSDetails {
  codice_sede: string | null;
  causale_contributo: string;
  matricola_inps_filiale_azienda: string | null;
  periodo_di_riferimento_da_mm_aaaa: string | null;
  periodo_di_riferimento_a_mm_aaaa: string | null;
  importo_a_debito_versato: string | null;
  importo_a_credito_compensato: string | null;
}

export interface RegioniDetails {
  codice_regione: string | null;
  codice_tributo: string;
  anno_riferimento?: string;
  importo_a_debito_versato: string | null;
  importo_a_credito_compensato: string | null;
  rateazione_mese_rif: string | null;
}

export interface IMUAltriEntiDetails {
  identificativo_operazione: string | null;
  codice_ente_locale: string | null;
  ravvedimento: boolean;
  immobili_variati: boolean;
  acconto: boolean;
  saldo: boolean;
  numero_immobili: number | null;
  codice_tributo: string;
  rateazione_mese_rif: string | null;
  anno_riferimento: string | null;
  importo_a_debito_versato: string | null;
  importo_a_credito_compensato: string | null;
}

export interface INAILAltriEntiDetails {
  codice_sede: string | null;
  codice_ditta: string | null;
  codice_controllo: string | null;
  numero_riferimento: string | null;
  causale: string;
  importo_a_debito_versato: string | null;
  importo_a_credito_compensato: string | null;
}

export interface AltriEntiDetails {
  codice_sede: string | null;
  codice_ente: string | null;
  causale_contributo: string;
  codice_posizione: string;
  periodo_di_riferimento_da_mm_aaaa: string | null;
  periodo_di_riferimento_a_mm_aaaa: string | null;
  importo_a_debito_versato: string | null;
  importo_a_credito_compensato: string | null;
}

interface ErarioTaxDebitCredit extends BaseTaxDebitCredit {
  sectionType: 'erario';
  details: ErarioDetails;
}

interface INPSTaxDebitCredit extends BaseTaxDebitCredit {
  sectionType: 'inps';
  details: INPSDetails;
}

interface RegioniTaxDebitCredit extends BaseTaxDebitCredit {
  sectionType: 'regioni';
  details: RegioniDetails;
}

interface IMUAltriEntiTaxDebitCredit extends BaseTaxDebitCredit {
  sectionType: 'imu_altri_enti';
  details: IMUAltriEntiDetails;
}

interface INAILAltriEntiTaxDebitCredit extends BaseTaxDebitCredit {
  sectionType: 'inail_altri_enti';
  details: INAILAltriEntiDetails;
}

interface AltriEntiTaxDebitCredit extends BaseTaxDebitCredit {
  sectionType: 'altri_enti';
  details: AltriEntiDetails;
}

export type TaxDebitCredit =
  | ErarioTaxDebitCredit
  | INPSTaxDebitCredit
  | RegioniTaxDebitCredit
  | IMUAltriEntiTaxDebitCredit
  | INAILAltriEntiTaxDebitCredit
  | AltriEntiTaxDebitCredit;

export interface TaxDebitCreditsQueryFilters {
  type?: 'debit' | 'credit' | null;
  fullyAllocated?: boolean;
  tribute?: string;
  dueDateFrom?: string | null;
  dueDateTo?: string | null;
}

export type CreateTaxDebitCreditFormInput =
  | ErarioDebitCreditFormInput
  | INPSTaxDebitCreditFormInput
  | RegioniTaxDebitCreditFormInput
  | IMUAltriEntiTaxDebitCreditFormInput
  | INAILAltriEntiTaxDebitCreditFormInput
  | AltriEntiTaxDebitCreditFormInput;

export type CreateTaxDebitCreditFormOutput =
  | ErarioDebitCreditFormOutput
  | INPSTaxDebitCreditFormOutput
  | RegioniTaxDebitCreditFormOutput
  | IMUAltriEntiTaxDebitCreditFormOutput
  | INAILAltriEntiTaxDebitCreditFormOutput
  | AltriEntiTaxDebitCreditFormOutput;

type DateFieldsToString<T> = Omit<T, 'scadenza' | 'validFrom' | 'validUntil'> & {
  scadenza?: string;
  validFrom?: string;
  validUntil?: string;
};
export type ErarioDebitCreditFormPayload = DateFieldsToString<ErarioDebitCreditFormOutput>;
export type INPSDebitCreditFormPayload = DateFieldsToString<INPSTaxDebitCreditFormOutput>;
export type RegionsDebitCreditFormPayload = DateFieldsToString<RegioniTaxDebitCreditFormOutput>;
export type IMUAltriEntiDebitCreditFormPayload = DateFieldsToString<IMUAltriEntiTaxDebitCreditFormOutput>;
export type INAILAltriEntiDebitCreditFormPayload = DateFieldsToString<INAILAltriEntiTaxDebitCreditFormOutput>;
export type AltriEntiDebitCreditFormPayload = DateFieldsToString<AltriEntiTaxDebitCreditFormOutput>;

export type CreateTaxDebitCreditFormPayload =
  | ErarioDebitCreditFormPayload
  | INPSDebitCreditFormPayload
  | RegionsDebitCreditFormPayload
  | IMUAltriEntiDebitCreditFormPayload
  | INAILAltriEntiDebitCreditFormPayload
  | AltriEntiDebitCreditFormPayload;

export const F24ResourceType = 'f24';

export interface F24CreatePayload {
  debits: TaxDebitCredit[];
  credits: TaxDebitCredit[];
  sanctions: CreateTaxDebitCreditFormPayload[];
  interests: CreateTaxDebitCreditFormPayload[];
  totalDebits: number;
  totalCredits: number;
  accountId: string;
  accountIban: string;
  paymentDate: Date;
  source: F24Source.BOOKKEEPING;
}

export interface F24ActivationData {
  conditionsAccepted: boolean;
  specificClausesAccepted: boolean;
}

interface F24UploadCreatePayloadBase {
  account?: {
    id: Account['id'];
  };
  name: string;
  source: F24Source.UPLOAD;
  paymentDate: Date;
  conditionsAccepted?: boolean;
  specificClausesAccepted?: boolean;
}

export interface F24UploadCreateFromOcrPayload extends F24UploadCreatePayloadBase {
  f24UploadId: string;
  pdfFilename?: string;
}

export type F24UploadCreatePayload = F24UploadCreateFromOcrPayload;

export enum F24Source {
  UPLOAD = 'UPLOAD',
  BOOKKEEPING = 'BOOKKEEPING',
}

export enum CodiceTributoSection {
  ERARIO = 'ERARIO',
  REGIONI = 'REGIONI',
  ENTI_LOCALI = 'ENTI_LOCALI',
}

export enum CodiceTributoModality {
  DEBIT,
  CREDIT,
}

export interface CodiceTributo {
  code: string;
  section: CodiceTributoSection;
  description: string;
  modalities: CodiceTributoModality[];
  reference: string;
  interest_tax_code: string;
  sanction_tax_code: string;
}

export interface CodiceSedeINPS {
  code: string;
  description: string;
}

export interface CausaleContributoINPS {
  code: string;
  description: string;
  modality: string;
}

export interface CodiceRegione {
  code: string;
  description: string;
}
